/**
 * The page format — the core contract shared by the CMS preview, the
 * compiler, the API and the browser renderer. See brief §3.
 */

/** A display row: exactly 40 codes, each 0x00–0x7F. */
export type Row = number[];

export const ROWS = 25;
export const COLS = 40;
export const FIRST_PAGE = 100;
export const LAST_PAGE = 899;

/** Fastext links: page numbers, or null when a key is unassigned. */
export interface Fastext {
  red: number | null;
  green: number | null;
  yellow: number | null;
  blue: number | null;
  index: number | null;
}

export type LinkTarget = { page: number; sub?: number } | { url: string };

/**
 * One hit target on the grid. `colEnd` is exclusive. Rows are 0-based
 * with 0 = header, so page data rows are 1–24 as on air.
 */
export interface Link {
  row: number;
  colStart: number;
  colEnd: number;
  target: LinkTarget;
  /** 'map' = explicit link map from the compiler, 'scan' = three-digit fallback, 'fastext' = row 24 label */
  origin?: 'map' | 'scan' | 'fastext';
}

/** Level 1.5 character override (packet X/26 equivalent). */
export interface Enhancement {
  row: number;
  col: number;
  /** A single Unicode character to display in place of the G0 glyph. */
  char: string;
}

export interface SubpageMeta {
  title?: string;
  source?: { name: string; url?: string };
  template?: string;
  generatedAt: string;
  publishFrom?: string;
  publishUntil?: string;
  revision: number;
  /** Cycle time in seconds for carousels; the renderer falls back to its default when absent. */
  cycleSeconds?: number;
}

/**
 * The on-wire subpage object returned by GET /pages/{ppp}/{sub}.
 *
 * `rows` holds 25 strings of exactly 40 characters; the character code of
 * each position is the teletext code (0x00–0x7F). Row 0 is present for
 * .tti compatibility but the renderer generates its own header.
 */
export interface Subpage {
  page: number;
  sub: number;
  subCount: number;
  rows: string[];
  fastext: Fastext;
  links: Link[];
  enhancements: Enhancement[];
  meta: SubpageMeta;
}

/** A page with all of its subpages — the unit of .tti import/export. */
export interface Page {
  page: number;
  subpages: Subpage[];
}

export interface PageSummary {
  page: number;
  subCount: number;
  title?: string;
  updatedAt: string;
}

export interface PageIndex {
  service: string;
  generatedAt: string;
  pages: PageSummary[];
}

export const EMPTY_FASTEXT: Fastext = { red: null, green: null, yellow: null, blue: null, index: null };

export function isPageNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= FIRST_PAGE && n <= LAST_PAGE;
}

/** Row codes → 40-char string (the JSON encoding). */
export function rowToString(row: Row): string {
  let s = '';
  for (let x = 0; x < COLS; x++) s += String.fromCharCode((row[x] ?? 0x20) & 0x7f);
  return s;
}

/** 40-char string → row codes. Short strings are space padded, long ones truncated. */
export function rowFromString(s: string): Row {
  const row: Row = new Array(COLS);
  for (let x = 0; x < COLS; x++) row[x] = x < s.length ? s.charCodeAt(x) & 0x7f : 0x20;
  return row;
}

export function blankRow(): Row {
  return new Array<number>(COLS).fill(0x20);
}

/** Subpage rows (25 strings) → 25 code rows. Missing rows are blank. */
export function subpageRows(sp: Pick<Subpage, 'rows'>): Row[] {
  const out: Row[] = [];
  for (let r = 0; r < ROWS; r++) {
    const s = sp.rows[r];
    out.push(s !== undefined ? rowFromString(s) : blankRow());
  }
  return out;
}
